import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import LanguageTabs, { useAvailableLanguages, initMultiLangField } from '../../components/admin/LanguageTabs';
import { 
  Plus, Save, Trash2, Edit2, X, Upload, Loader2, Eye, EyeOff, 
  Calendar, Repeat, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 1, label: 'Poniedziałek' },
  { value: 2, label: 'Wtorek' },
  { value: 3, label: 'Środa' },
  { value: 4, label: 'Czwartek' },
  { value: 5, label: 'Piątek' },
  { value: 6, label: 'Sobota' },
  { value: 0, label: 'Niedziela' }
];

const PREDEFINED_BADGES = ['BMX', 'Hulajnoga', 'Deskorolka', 'Rolki', 'Gokart'];

const AdminEvents = () => {
  const { token } = useAdminAuth();
  const { languages, loading: languagesLoading, defaultLanguage } = useAvailableLanguages();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [customBadge, setCustomBadge] = useState({});
  const [activeLangTab, setActiveLangTab] = useState(defaultLanguage || 'pl');

  const headers = { Authorization: `Bearer ${token}` };

  const getEmptyForm = () => ({
    title: initMultiLangField(languages),
    description: initMultiLangField(languages),
    date: '',
    time: '',
    endTime: '',
    price: '',
    isRecurring: false,
    recurringConfig: { startDate: '', endDate: '', daysOfWeek: [] },
    badges: [],
    isActive: true,
    image: ''
  });

  useEffect(() => { 
    if (!languagesLoading) {
      fetchEvents();
      setActiveLangTab(defaultLanguage || 'pl');
    }
  }, [languagesLoading]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/api/events/admin/all`, { headers });
      setEvents(res.data);
    } catch (err) {
      toast.error('Błąd pobierania wydarzeń');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingId(event.id);
    // Build form with all language fields
    const title = {};
    const description = {};
    languages.forEach(l => {
      title[l.code] = event.title?.[l.code] || '';
      description[l.code] = event.description?.[l.code] || '';
    });

    setForm({
      title,
      description,
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      time: event.time || '',
      endTime: event.endTime || '',
      price: event.price != null ? event.price.toString() : '',
      isRecurring: event.isRecurring || false,
      recurringConfig: event.recurringConfig || { startDate: '', endDate: '', daysOfWeek: [] },
      badges: event.badges || [],
      isActive: event.isActive !== false,
      image: event.image || ''
    });
    setImagePreview(event.image ? (event.image.startsWith('http') ? event.image : `${API}${event.image}`) : '');
    setImageFile(null);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(getEmptyForm());
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const toggleDayOfWeek = (day) => {
    setForm(prev => {
      const current = prev.recurringConfig.daysOfWeek || [];
      const newDays = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
      return { ...prev, recurringConfig: { ...prev.recurringConfig, daysOfWeek: newDays } };
    });
  };

  const addPredefinedBadge = (name) => {
    if (form.badges.some(b => (typeof b === 'string' ? b : b.pl) === name)) return;
    setForm(prev => ({ ...prev, badges: [...prev.badges, name] }));
  };

  const addCustomBadge = () => {
    if (!customBadge[defaultLanguage || 'pl']) return;
    setForm(prev => ({ ...prev, badges: [...prev.badges, { ...customBadge }] }));
    setCustomBadge({});
  };

  const removeBadge = (index) => {
    setForm(prev => ({ ...prev, badges: prev.badges.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    const defLang = defaultLanguage || 'pl';
    if (!form.title[defLang]) { toast.error(`Wpisz tytuł (${defLang.toUpperCase()})`); return; }
    if (!form.isRecurring && !form.date) { toast.error('Ustaw datę wydarzenia'); return; }
    if (form.isRecurring && (!form.recurringConfig.startDate || !form.recurringConfig.endDate)) {
      toast.error('Ustaw daty rozpoczęcia i zakończenia'); return;
    }
    if (form.isRecurring && form.recurringConfig.daysOfWeek.length === 0) {
      toast.error('Wybierz przynajmniej jeden dzień tygodnia'); return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      const data = {
        ...form,
        price: form.price !== '' ? parseFloat(form.price) : null
      };
      formData.append('data', JSON.stringify(data));
      if (imageFile) formData.append('image', imageFile);

      if (editingId) {
        await axios.put(`${API}/api/events/admin/${editingId}`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Wydarzenie zaktualizowane');
      } else {
        await axios.post(`${API}/api/events/admin`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Wydarzenie dodane');
      }
      setShowForm(false);
      setEditingId(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to wydarzenie?')) return;
    try {
      await axios.delete(`${API}/api/events/admin/${id}`, { headers });
      toast.success('Wydarzenie usunięte');
      fetchEvents();
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };

  const handleToggleActive = async (event) => {
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify({ isActive: !event.isActive }));
      await axios.put(`${API}/api/events/admin/${event.id}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchEvents();
    } catch (err) {
      toast.error('Błąd zmiany statusu');
    }
  };

  const getBadgeLabel = (badge) => {
    if (typeof badge === 'string') return badge;
    return badge[defaultLanguage] || badge.pl || badge.en || Object.values(badge)[0] || '';
  };

  if (loading || languagesLoading) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Wydarzenia</h1>
        {!showForm && (
          <Button onClick={handleNew} data-testid="add-event-btn">
            <Plus className="w-4 h-4 mr-2" /> Dodaj wydarzenie
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle>{editingId ? 'Edytuj wydarzenie' : 'Nowe wydarzenie'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dynamic language tabs */}
            <LanguageTabs
              activeTab={activeLangTab}
              onTabChange={setActiveLangTab}
              showIndicator={true}
              hasContent={Object.fromEntries(languages.map(l => [l.code, !!form.title?.[l.code]]))}
            />

            {/* Title */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Tytuł ({activeLangTab.toUpperCase()})</label>
              <Input
                value={form.title?.[activeLangTab] || ''}
                onChange={e => setForm(prev => ({ ...prev, title: { ...prev.title, [activeLangTab]: e.target.value } }))}
                placeholder={`Tytuł wydarzenia (${activeLangTab})`}
                data-testid="event-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Opis ({activeLangTab.toUpperCase()})</label>
              <textarea
                value={form.description?.[activeLangTab] || ''}
                onChange={e => setForm(prev => ({ ...prev, description: { ...prev.description, [activeLangTab]: e.target.value } }))}
                placeholder={`Opis wydarzenia (${activeLangTab})`}
                className="w-full min-h-[100px] bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                data-testid="event-desc-input"
              />
            </div>

            {/* Image */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Okładka (1:1)</label>
              <div className="flex items-center gap-4">
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg" />
                )}
                <label className="cursor-pointer bg-white/5 border border-white/10 rounded-lg px-4 py-2 hover:bg-white/10 transition-colors inline-flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Wybierz obraz
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              </div>
            </div>

            {/* Recurring toggle */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isRecurring}
                  onChange={e => setForm(prev => ({ ...prev, isRecurring: e.target.checked }))}
                  className="w-5 h-5 rounded"
                />
                <Repeat className="w-4 h-4" />
                <span>Wydarzenie cykliczne</span>
              </label>
            </div>

            {/* Date fields */}
            {form.isRecurring ? (
              <div className="space-y-4 p-4 bg-white/5 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Od daty</label>
                    <Input
                      type="date"
                      value={form.recurringConfig?.startDate || ''}
                      onChange={e => setForm(prev => ({ ...prev, recurringConfig: { ...prev.recurringConfig, startDate: e.target.value } }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Do daty</label>
                    <Input
                      type="date"
                      value={form.recurringConfig?.endDate || ''}
                      onChange={e => setForm(prev => ({ ...prev, recurringConfig: { ...prev.recurringConfig, endDate: e.target.value } }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Dni tygodnia</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDayOfWeek(day.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          (form.recurringConfig?.daysOfWeek || []).includes(day.value)
                            ? 'border-primary bg-primary/20 text-primary'
                            : 'border-white/10 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Data wydarzenia</label>
                <Input
                  type="date"
                  value={form.date || ''}
                  onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="event-date-input"
                />
              </div>
            )}

            {/* Time & Price */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Godzina start</label>
                <Input
                  type="time"
                  value={form.time || ''}
                  onChange={e => setForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Godzina koniec</label>
                <Input
                  type="time"
                  value={form.endTime || ''}
                  onChange={e => setForm(prev => ({ ...prev, endTime: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Cena (zł)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price || ''}
                  onChange={e => setForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0 = bezpłatne"
                />
              </div>
            </div>

            {/* Badges */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Kategorie / Badge</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {PREDEFINED_BADGES.map(name => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addPredefinedBadge(name)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      form.badges?.some(b => (typeof b === 'string' ? b : b.pl) === name)
                        ? 'border-primary bg-primary/20 text-primary'
                        : 'border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                  >
                    <Tag className="w-3 h-3 inline mr-1" /> {name}
                  </button>
                ))}
              </div>
              
              {/* Custom badge - dynamic language fields */}
              <div className="flex gap-2 items-end flex-wrap">
                {languages.filter(l => l.isActive).slice(0, 3).map(l => (
                  <div key={l.code} className="flex-1 min-w-[120px]">
                    <Input
                      value={customBadge[l.code] || ''}
                      onChange={e => setCustomBadge(prev => ({ ...prev, [l.code]: e.target.value }))}
                      placeholder={`Badge (${l.code.toUpperCase()})`}
                      className="text-sm"
                    />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addCustomBadge}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Selected badges */}
              {form.badges?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.badges.map((badge, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                      {getBadgeLabel(badge)}
                      <button type="button" onClick={() => removeBadge(i)} className="hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Zapisz zmiany' : 'Dodaj wydarzenie'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                <X className="w-4 h-4 mr-2" /> Anuluj
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events list */}
      {events.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-12 text-center text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            Brak wydarzeń. Dodaj pierwsze wydarzenie.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map(event => (
            <Card key={event.id} className={`mb-4 bg-white/5 border-white/10 ${!event.isActive ? 'opacity-50' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {event.image ? (
                    <img
                      src={event.image.startsWith('http') ? event.image : `${API}${event.image}`}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-gray-500" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium truncate">{event.title?.[defaultLanguage] || event.title?.pl || 'Bez tytułu'}</h3>
                      {event.isRecurring && <Repeat className="w-4 h-4 text-primary flex-shrink-0" />}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {event.badges?.map((badge, i) => (
                        <span key={i} className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                          {getBadgeLabel(badge)}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {event.isRecurring 
                        ? `Cykliczne: ${(event.recurringConfig?.daysOfWeek || []).map(d => DAYS_OF_WEEK.find(x => x.value === d)?.label).join(', ')}`
                        : event.date ? new Date(event.date).toLocaleDateString('pl-PL') : ''
                      }
                      {event.time && ` • ${event.time}`}
                      {event.price != null && event.price > 0 ? ` • ${event.price} zł` : ' • Bezpłatne'}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(event)}>
                      {event.isActive ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(event)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
